// Conversión de una imagen (logo del comercio) a un comando ESC/POS raster
// monocromo (GS v 0), para imprimir el logo en el ticket Bluetooth.
//
// Usa <canvas> del navegador: carga la imagen, la escala al ancho del rollo, la
// umbraliza a 1 bit (blanco/negro) y empaqueta los bits en el formato GS v 0.
//
// Es BEST-EFFORT: si la imagen no carga o el canvas queda "tainted" por CORS
// (`getImageData` lanza), devuelve null y el ticket se imprime SIN logo (no se
// rompe la impresión). No se accede a `document`/`Image` en el top-level.

// Ancho en puntos del cabezal según el rollo (estándar: 8 dots/mm efectivos).
export const dotsForWidth = (pageWidth: 58 | 80 | undefined): number => (pageWidth === 58 ? 384 : 576);

// Empaqueta un buffer RGBA (getImageData) a un comando ESC/POS raster GS v 0.
//
// - Umbral 1-bit por luminancia (< 128 ⇒ pixel negro); transparente → blanco.
// - Bits MSB-first dentro de cada byte; `bytesPerRow = ceil(w / 8)`.
// - Header GS v 0: 0x1d 0x76 0x30 0x00 xL xH yL yH (m=0, xBytes=bytesPerRow).
//
// El alto `h` se codifica en 16 bits (máx 65535 pts), suficiente para cualquier
// ticket; no requiere troceo (el envío se trocea aparte vía writeChunked).
export const imageDataToEscPosRaster = (data: Uint8ClampedArray, w: number, h: number): Uint8Array => {
  const bytesPerRow = Math.ceil(w / 8);
  const raster = new Uint8Array(bytesPerRow * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const alpha = data[i + 3];
      // Transparente → blanco; si no, luminancia. < 128 ⇒ pixel negro.
      const lum = alpha < 16 ? 255 : 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      if (lum < 128) raster[y * bytesPerRow + (x >> 3)] |= 0x80 >> (x & 7); // MSB-first
    }
  }
  const xBytes = bytesPerRow;
  const header = new Uint8Array([
    0x1d,
    0x76,
    0x30,
    0x00, // GS v 0 m=0
    xBytes & 0xff,
    (xBytes >> 8) & 0xff, // ancho en bytes
    h & 0xff,
    (h >> 8) & 0xff, // alto en puntos
  ]);
  const out = new Uint8Array(header.length + raster.length);
  out.set(header);
  out.set(raster, header.length);
  return out;
};

// Empaqueta a GS v 0 EN BANDAS verticales de `bandHeight` filas: cada banda lleva su PROPIO
// header GS v 0 + su sub-raster, y se concatenan. Las líneas impresas son IDÉNTICAS a un solo
// GS v 0 — solo cambia que el alto se declara en trozos chicos.
//
// Por qué: muchas térmicas BLE genéricas (PT-220 / "cat printer" / POS chino) NO materializan
// un GS v 0 con alto grande (spec Epson: "excess data not printed → split into 24-dot"; las
// libs de referencia python-escpos/Adafruit nunca mandan un raster gigante) → el ticket sale
// como ~2px por línea. Al trocear, cada banda declara un alto que SÍ cabe en el buffer del
// firmware y el papel avanza banda a banda (GS v 0 avanza exactamente su alto → bandas contiguas).
//
// `bandHeight <= 0` o `>= h` → delega en `imageDataToEscPosRaster` (sin bandas, BYTE-IDÉNTICO).
export const bandImageDataToEscPosRaster = (
  data: Uint8ClampedArray,
  w: number,
  h: number,
  bandHeight: number,
): Uint8Array => {
  if (bandHeight <= 0 || bandHeight >= h) return imageDataToEscPosRaster(data, w, h);
  const bytesPerRow = Math.ceil(w / 8);
  const parts: Uint8Array[] = [];
  for (let y0 = 0; y0 < h; y0 += bandHeight) {
    const bandH = Math.min(bandHeight, h - y0);
    const raster = new Uint8Array(bytesPerRow * bandH);
    for (let y = 0; y < bandH; y++) {
      const srcY = y0 + y;
      for (let x = 0; x < w; x++) {
        const i = (srcY * w + x) * 4;
        const alpha = data[i + 3];
        const lum = alpha < 16 ? 255 : 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        if (lum < 128) raster[y * bytesPerRow + (x >> 3)] |= 0x80 >> (x & 7); // MSB-first
      }
    }
    const header = new Uint8Array([
      0x1d,
      0x76,
      0x30,
      0x00, // GS v 0 m=0
      bytesPerRow & 0xff,
      (bytesPerRow >> 8) & 0xff,
      bandH & 0xff,
      (bandH >> 8) & 0xff,
    ]);
    const out = new Uint8Array(header.length + raster.length);
    out.set(header);
    out.set(raster, header.length);
    parts.push(out);
  }
  const total = parts.reduce((n, p) => n + p.length, 0);
  const all = new Uint8Array(total);
  let off = 0;
  for (const p of parts) {
    all.set(p, off);
    off += p.length;
  }
  return all;
};

// Empaqueta un buffer RGBA a una imagen ESC * (bit-image, modo 33 = 24-dot doble densidad),
// como ALTERNATIVA a GS v 0. Muchas térmicas BLE genéricas (PT-220 / "cat printer" / POS chino)
// NO materializan GS v 0: imprimen su payload como TEXTO (acentos/símbolos de la página de
// código). ESC * es el comando de gráficos clásico (Epson/Star), el más soportado por firmwares
// baratos, y lleva un CONTEO de bytes explícito → si el ancho no calza, la impresora trunca a su
// cabezal pero NUNCA vuelca bytes como texto.
//
// Formato por franja horizontal de 24 filas: `ESC * 33 nL nH` (n = ancho en dots) + por cada
// columna 3 bytes (24 bits verticales, MSB = fila superior) + LF. Antes de las franjas fija el
// interlineado a 24 dots (`ESC 3 24`) para que las franjas queden contiguas (sin rayas blancas)
// y al final restaura el interlineado por defecto (`ESC 2`). Umbral 1-bit idéntico (lum<128).
export const imageDataToEscPosStar = (data: Uint8ClampedArray, w: number, h: number): Uint8Array => {
  const ESC = 0x1b;
  const LF = 0x0a;
  const isBlack = (x: number, y: number): boolean => {
    if (y >= h) return false; // relleno blanco bajo la última franja
    const i = (y * w + x) * 4;
    const alpha = data[i + 3];
    const lum = alpha < 16 ? 255 : 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    return lum < 128;
  };
  const nL = w & 0xff;
  const nH = (w >> 8) & 0xff;
  const out: number[] = [ESC, 0x33, 24]; // ESC 3 24 → interlineado = 24 dots (franjas contiguas)
  for (let y0 = 0; y0 < h; y0 += 24) {
    out.push(ESC, 0x2a, 33, nL, nH); // ESC * m=33 nL nH
    for (let x = 0; x < w; x++) {
      let b0 = 0;
      let b1 = 0;
      let b2 = 0;
      for (let bit = 0; bit < 8; bit++) {
        if (isBlack(x, y0 + bit)) b0 |= 0x80 >> bit; // bytes 0..7  → filas 0..7  (MSB=arriba)
        if (isBlack(x, y0 + 8 + bit)) b1 |= 0x80 >> bit; // bytes 8..15 → filas 8..15
        if (isBlack(x, y0 + 16 + bit)) b2 |= 0x80 >> bit; // bytes 16..23 → filas 16..23
      }
      out.push(b0, b1, b2);
    }
    out.push(LF); // avanza exactamente 24 dots (por el interlineado fijado) → siguiente franja
  }
  out.push(ESC, 0x32); // ESC 2 → restaura interlineado por defecto
  return new Uint8Array(out);
};

// Igual que canvasToEscPosRaster pero emitiendo ESC * (bit-image) en vez de GS v 0. null si el
// canvas está tainted por CORS o no hay contexto 2D.
export const canvasToEscPosStar = (canvas: HTMLCanvasElement): Uint8Array | null => {
  const w = canvas.width;
  const h = canvas.height;
  if (w <= 0 || h <= 0) return null;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  try {
    const { data } = ctx.getImageData(0, 0, w, h);
    return imageDataToEscPosStar(data, w, h);
  } catch {
    return null;
  }
};

// Devuelve el comando GS v 0 (raster) de un canvas ya dibujado (negro sobre
// blanco), o null si el canvas está "tainted" por CORS (`getImageData` lanza) o
// no hay contexto 2D. El renderer del ticket-canvas usa esto.
//
// `bandHeight > 0` trocea el raster en bandas (compat. con térmicas que no soportan un GS v 0
// alto). Default 0 = un solo GS v 0 (comportamiento previo, byte-idéntico).
export const canvasToEscPosRaster = (canvas: HTMLCanvasElement, bandHeight = 0): Uint8Array | null => {
  const w = canvas.width;
  const h = canvas.height;
  if (w <= 0 || h <= 0) return null;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  try {
    const { data } = ctx.getImageData(0, 0, w, h); // lanza si está tainted por CORS
    return bandImageDataToEscPosRaster(data, w, h, bandHeight);
  } catch {
    return null;
  }
};

const loadImageEl = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('No se pudo cargar la imagen del logo.'));
    img.src = url;
  });

// Carga una imagen para dibujarla en <canvas> SIN depender del CORS del canvas.
//
// Vía robusta: `fetch(mode:'cors')` → `blob` → `createImageBitmap`. Un bitmap creado
// desde un blob YA descargado NO "taintea" el canvas (el blob es un recurso local),
// aunque el origen sea cross-origin. Además `cache:'reload'` evita reutilizar la
// entrada de caché "envenenada" por el `<img>` SIN `Origin` que pinta el NavBar (que
// dejaba en caché la variante de la respuesta sin `Access-Control-Allow-Origin`).
//
// Fallback: `<img crossOrigin>` (que SÍ puede taintear) — el caller debe AISLAR/PROBAR
// el resultado (dibujarlo en un canvas aparte y probar getImageData) para no romper.
export const loadDrawableImage = async (url: string): Promise<ImageBitmap | HTMLImageElement> => {
  if (typeof fetch !== 'undefined' && typeof createImageBitmap === 'function') {
    try {
      const res = await fetch(url, { mode: 'cors', cache: 'reload' });
      if (res.ok) {
        const blob = await res.blob();
        return await createImageBitmap(blob);
      }
    } catch {
      /* cae al <img crossOrigin> */
    }
  }
  return loadImageEl(url);
};

// Probe del logo para la VISTA PREVIA de "Solo texto": carga el logo (loadDrawableImage),
// lo umbraliza a 1-bit (lum<128, EXACTAMENTE el mismo umbral que imageDataToEscPosRaster usa
// para la impresión) y PRUEBA getImageData (lanza si quedó tainted por CORS). Devuelve un
// dataURL PNG del bitmap B/N 1-bit REAL (no grayscale CSS) que el preview pinta, o null si el
// probe falla (taint CORS) → el preview oculta el logo, EXACTAMENTE como la impresión lo omite.
// Así el preview de texto refleja preview==impresión==prueba (decisión 1A): el mismo pipeline
// que decide "¿se imprime el logo?" decide "¿se muestra en el preview?".
export const probeLogo1Bit = async (url: string, maxWidthDots: number): Promise<string | null> => {
  if (typeof document === 'undefined' || typeof Image === 'undefined') return null;
  try {
    const img = await loadDrawableImage(url);
    const ratio = Math.min(1, maxWidthDots / (img.width || maxWidthDots));
    const w = Math.max(1, Math.round((img.width || maxWidthDots) * ratio));
    const h = Math.max(1, Math.round((img.height || 1) * ratio));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    const imageData = ctx.getImageData(0, 0, w, h); // lanza si está tainted por CORS → catch → null
    const { data } = imageData;
    // Umbraliza in-place a blanco/negro puro con el MISMO criterio que la impresión: <128 → negro.
    for (let i = 0; i < data.length; i += 4) {
      const alpha = data[i + 3];
      const lum = alpha < 16 ? 255 : 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      const v = lum < 128 ? 0 : 255;
      data[i] = v;
      data[i + 1] = v;
      data[i + 2] = v;
      data[i + 3] = 255;
    }
    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
};

// Devuelve el comando GS v 0 (raster) del logo escalado a `maxWidthDots`, o null
// si la imagen no es accesible/convertible. Ya está aislado (canvas propio + try/catch).
export const imageToEscPosRaster = async (url: string, maxWidthDots: number): Promise<Uint8Array | null> => {
  if (typeof document === 'undefined' || typeof Image === 'undefined') return null;
  try {
    const img = await loadDrawableImage(url);
    const ratio = Math.min(1, maxWidthDots / (img.width || maxWidthDots));
    const w = Math.max(1, Math.round((img.width || maxWidthDots) * ratio));
    const h = Math.max(1, Math.round((img.height || 1) * ratio));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h); // lanza si está tainted por CORS
    return imageDataToEscPosRaster(data, w, h);
  } catch {
    return null;
  }
};
